import {trackEvent, type TrackEventData} from '@hconnect/common/logging/Analytics'
import {useUserContext} from '@hconnect/login'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import {useCallback} from 'react'

import {type PlantStatusAnalyticsEvent} from '../types'

const PRODUCT = 'hproduce'
const SUB_PRODUCT = 'hroc'
const FEATURE = 'Plant Entry Point'

type AnalyticsEventName = PlantStatusAnalyticsEvent['name']

type BaseAnalyticsData = {
  timestamp: string
  userId?: string
  country?: string | null
}

type PlantAnalyticsData = {
  plantId: string
  plantName: string
}

type EventSpecificData<T extends AnalyticsEventName> =
  Extract<PlantStatusAnalyticsEvent, {name: T}> extends {
    data: infer D extends Record<string, unknown>
  }
    ? D
    : Record<string, never>

const trackPlantStatusAnalyticsEvent = (eventName: AnalyticsEventName, data: TrackEventData) =>
  trackEvent(eventName, {product: PRODUCT, subProduct: SUB_PRODUCT, feature: FEATURE, ...data})

export const useTrackPlantStatusAnalyticsEvent = (plantId: string) => {
  const upmPlants = useUpmPlantsContext()
  const {loggedUser} = useUserContext()
  const userId = loggedUser?.user_id
  const country = loggedUser?.country

  return useCallback(
    <T extends AnalyticsEventName>(eventName: T, data: EventSpecificData<T>) => {
      const commonEventData: BaseAnalyticsData = {
        timestamp: new Date().toISOString(),
        userId,
        country
      }
      const plantData: PlantAnalyticsData = {
        plantId,
        plantName: getPlantName(upmPlants, plantId)
      }

      trackPlantStatusAnalyticsEvent(eventName, {
        ...commonEventData,
        ...plantData,
        ...data
      })
    },
    [plantId, userId, country, upmPlants]
  )
}
